package com.snakesworld.game.engine

import kotlinx.serialization.Serializable
import kotlin.math.atan2
import kotlin.math.sqrt

/**
 * 2D Vector representation for position, velocity, and distance calculations.
 */
@Serializable
data class Vector2D(
    val x: Float = 0f,
    val y: Float = 0f,
) {
    operator fun plus(other: Vector2D): Vector2D = Vector2D(x + other.x, y + other.y)
    operator fun minus(other: Vector2D): Vector2D = Vector2D(x - other.x, y - other.y)
    operator fun times(scalar: Float): Vector2D = Vector2D(x * scalar, y * scalar)
    operator fun div(scalar: Float): Vector2D = if (scalar == 0f) ZERO else Vector2D(x / scalar, y / scalar)

    fun length(): Float = sqrt(x * x + y * y)
    fun lengthSquared(): Float = x * x + y * y

    fun distanceTo(other: Vector2D): Float = (this - other).length()
    fun distanceSquaredTo(other: Vector2D): Float = (this - other).lengthSquared()

    fun normalized(): Vector2D {
        val len = length()
        return if (len > 0.0001f) this / len else ZERO
    }

    fun angle(): Float = atan2(y, x)

    fun lerp(target: Vector2D, t: Float): Vector2D {
        val clampedT = t.coerceIn(0f, 1f)
        return Vector2D(x + (target.x - x) * clampedT, y + (target.y - y) * clampedT)
    }

    companion object {
        val ZERO = Vector2D(0f, 0f)
    }
}
